import type { SitesBulkResponse } from './types';

import {
  type DefaultError,
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysSite } from '../queryKeys';

import { fetchSitesBulk } from './fetchSitesBulk';

/**
 * Builds the shared query configuration for a permission-filtered bulk site lookup.
 *
 * @param siteIds - Site IDs to query, or undefined to disable the query.
 * @param options - Additional query options.
 * @returns Typed TanStack Query options.
 */
export const getSitesBulkQueryOptions = <TData = SitesBulkResponse>({
  siteIds,
  options,
}: {
  siteIds: UUID[] | undefined;
  options?: Omit<
    UseQueryOptions<SitesBulkResponse, DefaultError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: siteIds?.length
      ? ({ signal }: QueryFunctionContext) =>
          fetchSitesBulk(siteIds, { signal })
      : skipToken,
    queryKey: queryKeysSite.getBulk(siteIds ?? []),
    staleTime: ms('30s'),
    ...options,
  });

/**
 * Queries permission-filtered site data for a list of site IDs.
 *
 * @param siteIds - Site IDs to query, or undefined to disable the query.
 * @param options - Additional query options.
 * @returns The bulk site query result.
 */
// fallow-ignore-next-line unused-export -- public hook API for bulk site consumers
export const useQuerySitesBulk = <TData = SitesBulkResponse>(
  siteIds: UUID[] | undefined,
  options?: Omit<
    UseQueryOptions<SitesBulkResponse, DefaultError, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getSitesBulkQueryOptions({ siteIds, options }));
